import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { css } from '@emotion/react';
import { useCallback, useEffect, useRef } from 'react';
import { createSetDeviceIdOutbound, createSetWebClientTypeOutbound } from '../../api/outbound/entryOutbounds';
import { createInitLobbyOutbound } from '../../api/outbound/lobbyOutbounds';
import TRIPLEJACK_LOGO_IMAGE_URL from '../../assets/tj-logo.png';
import { startLoadingAllBombsInBackground } from '../../bombs/bombLoaderUtils';
import { useInstanceId } from '../../context/instance/instanceContext';
import { useSocketSend } from '../../context/webSocket/webSocketContext';
import { useAppDispatch } from '../../redux/store';
import { PROGRESS_BAR_BACKGROUND_COLOR, PROGRESS_BAR_COLOR } from '../../styles/colors';
import { getRDDResult } from '../../types/webClientTypes';
import { initBitmapFonts } from '../../utils/bitmapFontUtils';
import { calcFingerprintDeviceId } from '../../utils/fingerprintUtils';
import { loadAssets } from '../../utils/loaderUtils';
import { initPixiSettings } from '../../utils/pixiUtils';
import { delay } from '../../utils/utils';
const PROGRESS_BAR_WIDTH_PX = 300;
const PROGRESS_BAR_HEIGHT_PX = 30;
const BORDER_RADIUS_PX = 5;
const LOADING_SCENE_MINIMUM_MS = 100;
const CONTAINER_CSS = css `
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;
const INNER_CONTAINER_CSS = css `
  display: flex;
  flex-direction: column;
  align-items: center;
`;
const LOADING_TEXT_CSS = css `
  color: white;
`;
const PROGRESS_BAR_BACKGROUND_CSS = css `
  background-color: ${PROGRESS_BAR_BACKGROUND_COLOR};
  height: ${PROGRESS_BAR_HEIGHT_PX}px;
  width: ${PROGRESS_BAR_WIDTH_PX}px;
  border-radius: ${BORDER_RADIUS_PX}px;
`;
const PROGRESS_BAR_CSS = css `
  background-color: ${PROGRESS_BAR_COLOR};
  height: ${PROGRESS_BAR_HEIGHT_PX}px;
  // width will be adjusted dynamically as assets are loaded
  width: 0;
  border-radius: ${BORDER_RADIUS_PX}px;
`;
async function getMinimumSceneDelayPromise(instanceId) {
    if (instanceId !== 0) {
        // only bother doing an artificial loading scene delay for the first instance
        return;
    }
    return delay(LOADING_SCENE_MINIMUM_MS);
}
export const FullLoadingScene = () => {
    const progressBarRef = useRef(null);
    const dispatch = useAppDispatch();
    const instanceId = useInstanceId();
    const socketSend = useSocketSend();
    const handleLoadedAsset = useCallback((numLoaded, numTotal) => {
        // When loading each asset is complete, the numLoaded may be 1 or more higher than numTotal.
        // TODO: Determine why.
        const newRatioDone = Math.min(1, numLoaded / numTotal);
        if (progressBarRef.current) {
            progressBarRef.current.style.width = `${PROGRESS_BAR_WIDTH_PX * newRatioDone}px`;
        }
    }, []);
    useEffect(() => {
        const effect = async () => {
            const minimumSceneDelayPromise = getMinimumSceneDelayPromise(instanceId);
            initPixiSettings(instanceId);
            await loadAssets(handleLoadedAsset);
            // do not await bomb loading here so it can be done in the background and won't block the main game preload:
            startLoadingAllBombsInBackground(dispatch, instanceId);
            socketSend(createSetWebClientTypeOutbound(getRDDResult()));
            const deviceId = await calcFingerprintDeviceId();
            await initBitmapFonts();
            await minimumSceneDelayPromise;
            socketSend(createInitLobbyOutbound());
            // must be logged in (lobby initialized) to record device ID since the server needs to have initialized the player ID at that time:
            // TODO: Consider doing this at a different time, as there could be a race condition with initializing the lobby (above) and then somehow
            // not being in the lobby by the time the device ID is being processed.
            socketSend(createSetDeviceIdOutbound(deviceId));
        };
        effect();
    }, [handleLoadedAsset, instanceId, socketSend, dispatch]);
    return (_jsx("div", { css: CONTAINER_CSS, children: _jsxs("div", { css: INNER_CONTAINER_CSS, children: [_jsx("img", { src: TRIPLEJACK_LOGO_IMAGE_URL }), _jsx("h3", { css: LOADING_TEXT_CSS, children: "Loading..." }), _jsx("div", { css: PROGRESS_BAR_BACKGROUND_CSS, children: _jsx("div", { ref: progressBarRef, css: PROGRESS_BAR_CSS }) })] }) }));
};
